import React, { useState } from 'react';
import { X, Battery, Zap, MapPin } from 'lucide-react';

interface BikeModel {
  id: string;
  name: string;
  type: string;
  image: string;
  price: string;
  range: string;
  power: string;
  charging: string;
  description: string;
  features: string[];
}

const models: BikeModel[] = [
  {
    id: 'city-glider',
    name: 'City Glider',
    type: 'Urban Commuter',
    image: 'https://images.pexels.com/photos/1149601/pexels-photo-1149601.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    price: '$1,899',
    range: '100 km',
    power: '350W',
    charging: '3 hours',
    description: 'Perfect for daily commutes and urban adventures, the City Glider combines style and functionality in one sleek package.',
    features: [
      'Lightweight aluminum frame',
      'Integrated front and rear lights',
      'Smartphone connectivity',
      'Hydraulic disc brakes',
      '7-speed Shimano gears',
      'Ergonomic saddle for maximum comfort'
    ]
  },
  {
    id: 'mountain-explorer',
    name: 'Mountain Explorer',
    type: 'Off-road Performance',
    image: 'https://images.pexels.com/photos/100582/pexels-photo-100582.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    price: '$2,499',
    range: '80 km',
    power: '500W',
    charging: '3.5 hours',
    description: 'Conquer any terrain with the rugged Mountain Explorer, designed for adventure seekers and off-road enthusiasts.',
    features: [
      'Full suspension system',
      'Heavy-duty frame and components',
      'High-torque motor for steep climbs',
      'All-terrain tires',
      '9-speed professional gear system',
      'Advanced shock absorption'
    ]
  },
  {
    id: 'fold-go',
    name: 'Fold & Go',
    type: 'Compact & Portable',
    image: 'https://images.pexels.com/photos/4990542/pexels-photo-4990542.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    price: '$1,699',
    range: '60 km',
    power: '250W',
    charging: '2.5 hours',
    description: 'The ultimate portable solution that folds in seconds, perfect for mixed commutes and limited storage spaces.',
    features: [
      'Quick-fold mechanism (under 10 seconds)',
      'Compact design for easy storage',
      'Fits in car trunks and under desks',
      'Weighs just 16kg with battery',
      'Smooth ride despite smaller wheels',
      'Built-in carrying handle'
    ]
  }
];

const ModelCard: React.FC<{ model: BikeModel; onClick: () => void }> = ({ model, onClick }) => {
  return (
    <div 
      className="bg-white rounded-lg overflow-hidden shadow-lg transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1"
      onClick={onClick}
    >
      <div 
        className="h-48 bg-cover bg-center cursor-pointer" 
        style={{ backgroundImage: `url(${model.image})` }}
      />
      <div className="p-6">
        <div className="flex justify-between items-start mb-2">
          <div>
            <h3 className="text-xl font-bold text-gray-800">{model.name}</h3>
            <p className="text-primary text-sm font-medium">{model.type}</p>
          </div>
          <span className="text-accent font-bold">{model.price}</span>
        </div>
        
        <div className="flex space-x-4 mb-4">
          <div className="flex items-center">
            <Battery size={16} className="text-primary mr-1" />
            <span className="text-sm text-gray-600">{model.range}</span>
          </div>
          <div className="flex items-center">
            <Zap size={16} className="text-primary mr-1" />
            <span className="text-sm text-gray-600">{model.power}</span>
          </div>
        </div>
        
        <p className="text-gray-600 mb-4 line-clamp-2">{model.description}</p>
        
        <button 
          className="w-full bg-primary hover:bg-primary-dark text-white py-2 rounded-full transition-colors"
          onClick={onClick}
        >
          View Details
        </button>
      </div>
    </div>
  );
};

const ModelDetails: React.FC<{ model: BikeModel; onClose: () => void }> = ({ model, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto" 
        onClick={e => e.stopPropagation()}
      >
        <div className="relative">
          <img 
            src={model.image} 
            alt={model.name} 
            className="w-full h-64 md:h-80 object-cover"
          />
          <button 
            className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-800">{model.name}</h3>
              <p className="text-primary font-medium">{model.type}</p>
            </div>
            <div className="text-right">
              <div className="text-accent text-2xl font-bold">{model.price}</div>
              <p className="text-gray-500 text-sm">Financing available</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
              <Battery size={24} className="text-primary mr-3" />
              <div>
                <p className="text-sm text-gray-500">Range</p>
                <p className="font-medium">{model.range}</p>
              </div>
            </div>
            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
              <Zap size={24} className="text-primary mr-3" />
              <div>
                <p className="text-sm text-gray-500">Motor Power</p>
                <p className="font-medium">{model.power}</p>
              </div>
            </div>
            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
              <MapPin size={24} className="text-primary mr-3" />
              <div>
                <p className="text-sm text-gray-500">Charging Time</p>
                <p className="font-medium">{model.charging}</p>
              </div>
            </div>
          </div>
          
          <div className="mb-6">
            <h4 className="text-lg font-bold text-gray-800 mb-2">Description</h4>
            <p className="text-gray-600">{model.description}</p>
          </div>
          
          <div className="mb-6">
            <h4 className="text-lg font-bold text-gray-800 mb-2">Key Features</h4>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {model.features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span className="text-gray-600">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <button 
              className="flex-1 bg-accent hover:bg-accent-dark text-white py-3 px-6 rounded-full font-bold transition-colors"
              onClick={() => {
                onClose();
                const contactElement = document.getElementById('contact');
                if (contactElement) {
                  contactElement.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              Book a Test Ride
            </button>
            <button 
              className="flex-1 border border-primary text-primary hover:bg-primary hover:text-white py-3 px-6 rounded-full font-bold transition-colors"
              onClick={onClose}
            >
              Download Brochure
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ModelsSection: React.FC = () => {
  const [selectedModel, setSelectedModel] = useState<BikeModel | null>(null);

  return (
    <section id="models" className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Featured Models</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Explore our range of premium electric bicycles, each designed with specific riding styles and needs in mind.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {models.map(model => (
            <ModelCard 
              key={model.id} 
              model={model} 
              onClick={() => setSelectedModel(model)}
            />
          ))}
        </div>
      </div>
      
      {selectedModel && (
        <ModelDetails model={selectedModel} onClose={() => setSelectedModel(null)} />
      )}
    </section>
  );
};

export default ModelsSection;